import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useAgentTenderFeed } from "../../hooks/agent-tender/useAgentTenderFeed";
import AgentTenderCard from "../../components/agent-tender/AgentTenderCard";
import AgentTenderFilterModal from "../../components/agent-tender/AgentTenderFilterModal";
import AgentRequestListView from "../../components/agent-tender/AgentRequestListView";
import MyAgentTenders from "./MyAgentTenders";
import CustomDropdown from "../../components/common/CustomDropdown";
import InfoDot from "../../components/common/InfoDot";
import v2DropdownProps from "../../components/agent-tender/v2DropdownProps";
import CategoryPills from "../../components/agent-tender/CategoryPills";
import {
  FEED_LEVEL_CHOICES,
  FEED_SORT_CHOICES,
} from "../../constants/agentTenderChoices";

// =====================================================================
// Agent Requests feed — V2 UI rebuild 1:1 from design doc 01 (brief
// BRIEF_V2_UI_REBUILD, Marin 2026-07-22). Presentation layer only: all
// state/behavior lives in useAgentTenderFeed and is untouched.
//
// Artboard grammar (all seven widths 390/768/1024/1280/1440/1920/2560):
//   title + compact "Post" pill (plus glyph) right of the title ·
//   UNDERLINE tabs My Requests / All Listed / Saved (bookmark + count when
//   active) · ONE nowrap category-pill row with the Cards/List toggle at its
//   right (toggle hidden <500px) · filters in ONE row ≥1024, exactly TWO
//   compact rows below · hint line with the green verified-shield glyph ·
//   grid 1/2/2/3/3/4/5 columns, capped 1800px @1920 and 2160px @2560.
// Every SVG below is copied verbatim from the artboards (brief RULE 2).
// =====================================================================

// ── Verbatim artboard glyphs ─────────────────────────────────────────

// Plus (the Post pill).
const PlusGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

// Magnifier (filter search input).
const SearchGlyph = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </svg>
);

// Filter funnel (Filters button + mobile filter icon button).
const FunnelGlyph = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M4 5h16l-6 7v5l-4 2v-7z" />
  </svg>
);

// Dropdown chevron.
const ChevronGlyph = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

// Green verified shield (feed hint line, #047857 — micro-detail per brief).
const HintShieldGlyph = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2l7 3v6c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V5z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

// Filled bookmark (active Saved tab).
const BookmarkFillGlyph = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#2283FF" aria-hidden="true">
    <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z" />
  </svg>
);

// Cards/List toggle glyphs.
const GridGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);
const ListGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

// ── Page fragments ───────────────────────────────────────────────────

// Top tabs mirroring the classic Requests page's My/All/Saved arrangement
// (DoD 12) — V2 UNDERLINE style per design doc 01: 1px row rule, active tab
// bold with a 2px #2283FF underline; the active Saved tab carries the filled
// bookmark glyph and its count badge.
const TOP_TABS = [
  { key: "my_requests", label: "My Requests" },
  { key: "all_listed", label: "All Listed" },
  { key: "saved", label: "Saved" },
];

const TopTabs = ({ active, onChange, savedCount }) => (
  <div className="flex gap-[22px] mt-3 border-b border-[#E2E8F0]">
    {TOP_TABS.map((t) => {
      const isActive = active === t.key;
      const isSavedActive = isActive && t.key === "saved";
      return (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          className={`inline-flex items-center gap-[5px] text-[13.5px] pb-[9px] -mb-px transition-colors ${
            isActive
              ? "font-bold text-[#0F172A] border-b-2 border-[#2283FF]"
              : "font-medium text-[#64748B] hover:text-[#334155]"
          }`}
        >
          {isSavedActive && <BookmarkFillGlyph />}
          {t.label}
          {isSavedActive && savedCount > 0 && (
            <span className="text-[11px] font-extrabold bg-[#2283FF] text-white rounded-full px-[7px] py-px">
              {savedCount}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

// Cards/List toggle — two 30px icon cells (artboard); hidden below 500px
// where the feed always keeps cards.
const FeedViewToggle = ({ value, onChange }) => {
  const cell = (mode, glyph, label) => {
    const isActive = (value === "list" ? "list" : "card") === mode;
    return (
      <button
        type="button"
        onClick={() => onChange(mode)}
        aria-pressed={isActive}
        aria-label={label}
        title={label}
        className={`w-[30px] h-[30px] inline-flex items-center justify-center ${
          isActive ? "bg-[#E8F1FF] text-[#1B6FE0]" : "bg-white text-[#64748B]"
        }`}
      >
        {glyph}
      </button>
    );
  };
  return (
    <span className="hidden min-[500px]:inline-flex flex-none border border-[#E2E8F0] rounded-lg overflow-hidden">
      {cell("card", <GridGlyph />, "Card view")}
      {cell("list", <ListGlyph />, "List view")}
    </span>
  );
};

// All/Buy/Loan segmented control (artboard: #F1F5F9 track, white active chip).
// `compact` = the two-row mobile scale (12px / 32px).
const SEGMENTS = [
  { value: "all", label: "All" },
  { value: "buy", label: "Buy" },
  { value: "loan", label: "Loan" },
];

const FeedSegmented = ({ value, onChange, compact = false }) => {
  const current = value || "all";
  return (
    <span className="inline-flex flex-none bg-[#F1F5F9] rounded-[9px] p-0.5">
      {SEGMENTS.map((s) => (
        <button
          key={s.value}
          type="button"
          onClick={() => onChange(s.value)}
          className={`${compact ? "text-[12px] px-[11px] py-1" : "text-[12.5px] px-3 py-[5px]"} rounded-[7px] transition-colors ${
            current === s.value
              ? "font-semibold bg-white shadow-[0_1px_2px_rgba(15,23,42,0.08)] text-[#0F172A]"
              : "font-medium text-[#64748B]"
          }`}
        >
          {s.label}
        </button>
      ))}
    </span>
  );
};

const Pagination = ({ pagination, onPage }) => {
  if (!pagination || pagination.count <= 0) return null;
  const { currentPage, next, previous } = pagination;
  if (!next && !previous) return null;
  return (
    <div className="flex justify-center items-center gap-4 mt-8">
      <button
        disabled={!previous}
        onClick={() => onPage(currentPage - 1)}
        className="px-4 py-2 border rounded-lg disabled:opacity-40 hover:bg-gray-50"
      >
        Previous
      </button>
      <span className="text-sm text-gray-600">Page {currentPage}</span>
      <button
        disabled={!next}
        onClick={() => onPage(currentPage + 1)}
        className="px-4 py-2 border rounded-lg disabled:opacity-40 hover:bg-gray-50"
      >
        Next
      </button>
    </div>
  );
};

// Saved tab empty state — design doc 01 ("the bookmark explains itself"):
// bookmark glyph, the notify promise, and a way back to All Listed.
const SavedEmptyState = ({ onBrowse }) => (
  <div className="mt-6 bg-white border border-dashed border-[#E2E8F0] rounded-[14px] px-5 py-9 flex flex-col items-center gap-2.5 text-center">
    <span className="w-11 h-11 rounded-full bg-[#E8F1FF] inline-flex items-center justify-center text-[#2283FF]">
      <svg
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinejoin="round"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z" />
      </svg>
    </span>
    <div className="text-[15px] font-bold text-[#0F172A]">No saved requests yet</div>
    <p className="text-[12.5px] text-[#64748B] max-w-[240px]">
      Tap the bookmark on any request to keep it here. Saved requests notify
      you when they get verified or superseded.
    </p>
    <button
      type="button"
      onClick={onBrowse}
      className="mt-1 border border-[#E2E8F0] bg-white text-[#334155] text-[12.5px] font-semibold h-[38px] px-4 rounded-[9px] hover:bg-gray-50"
    >
      Browse All Listed
    </button>
  </div>
);

const EmptyState = ({ verifiedOnly, hasFilters }) => {
  let title = "No agent requests yet";
  let body =
    "There are no agent requests to show right now. Check back soon or post your own.";
  if (verifiedOnly) {
    title = "No verified requests yet";
    body =
      'No club-verified requests match your filters right now. Turn off "Verified only" to see all requests.';
  } else if (hasFilters) {
    title = "No matching requests";
    body = "No agent requests match your filters. Try widening your search.";
  }
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="bg-gray-100 rounded-full p-4 mb-4">
        <Search className="w-8 h-8 text-gray-500" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-1">{title}</h3>
      <p className="text-gray-500 max-w-sm">{body}</p>
    </div>
  );
};

// Mobile sort options carry the artboard's shortened "Newest" trigger label
// (two-row filter board); the values are identical to FEED_SORT_CHOICES.
const FEED_SORT_CHOICES_COMPACT = FEED_SORT_CHOICES.map((o) =>
  o.value === "newest" ? { ...o, label: "Newest" } : o,
);

const AgentTenderFeed = () => {
  const navigate = useNavigate();
  const {
    tenders,
    loading,
    error,
    showFilterModal,
    setShowFilterModal,
    filters,
    applyFilters,
    activeCategory,
    changeCategory,
    tabCounts,
    showSaved,
    toggleSave,
    savingIds,
    viewMode,
    changeViewMode,
    toggleVerifiedOnly,
    hasActiveFilters,
    clearFilters,
    searchTerm,
    setSearchTerm,
    pagination,
    fetchTenders,
  } = useAgentTenderFeed();

  // Top-tab shell: All Listed (browse) / My Requests (own) / Saved (?saved=true).
  const [topTab, setTopTab] = useState("all_listed");
  const selectTab = (t) => {
    setTopTab(t);
    if (t === "all_listed") showSaved(false);
    else if (t === "saved") showSaved(true);
  };
  const isMine = topTab === "my_requests";

  const goPitch = (t) =>
    navigate(`/agent-tenders/${t.id}/pitch`, { state: { tender: t } });

  // Shared filter fragments (desktop one-row / mobile two-row).
  const searchInput = (
    <div className="relative flex-1 lg:max-w-[320px] min-w-0">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]">
        <SearchGlyph size={14} />
      </span>
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search position or agency"
        className="w-full h-9 pl-9 pr-3 text-[13px] bg-white border border-[#E2E8F0] rounded-[9px] placeholder:text-[#94A3B8] focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
    </div>
  );

  const clearAllButton = hasActiveFilters && (
    <button
      onClick={clearFilters}
      className="flex-none px-2 text-[12.5px] text-red-500 hover:text-red-700 whitespace-nowrap"
    >
      Clear all
    </button>
  );

  return (
    // `min-h-full` (not min-h-screen) fills the layout's content region without
    // overflowing past the viewport — the page scrolls as one document (A1).
    // Horizontal padding follows the artboard scale (16/24/28/32/40/48); the
    // bottom padding keeps the floating support button clear of the last row.
    <div className="min-h-full bg-gray-50">
      <div className="mx-auto px-4 md:px-6 lg:px-7 xl:px-8 min-[1920px]:px-10 min-[2560px]:px-12 pt-[18px] pb-24">
        {/* Header — title + COMPACT Post pill in one row at every width */}
        <div className="flex items-center gap-3">
          {/* InfoDot glued to the title's last word: the h1 is an inline-flex
              nowrap row (gap-2 = the design's 8px), so the dot is vertically
              centered on the title line and can never wrap alone. The title
              stays ONE text node so the DOM translator keeps matching the
              bundled "Agent Requests" key. */}
          <h1 className="inline-flex items-center gap-2 whitespace-nowrap text-[22px] font-extrabold tracking-[-0.02em] text-[#0F172A]">
            Agent Requests
            <InfoDot
              title="Agent Requests"
              body="Agent Requests lets player agencies ask a club to work together on one of its transfer listings. It's for agencies that want to pitch or represent a listed player. The club reviews each request and can accept or decline."
            />
          </h1>
          <span className="flex-1" />
          <button
            onClick={() => navigate("/agent-tenders/create")}
            className="flex-none inline-flex items-center gap-1.5 bg-[#2283FF] text-white text-[13px] font-semibold px-3.5 h-9 rounded-[9px] hover:bg-[#1B6FE0]"
          >
            <PlusGlyph />
            <span className="hidden md:inline">Post Request</span>
            <span className="md:hidden">Post</span>
          </button>
        </div>

        {/* Top tabs — underline style (design doc 01) */}
        <TopTabs
          active={topTab}
          onChange={selectTab}
          savedCount={topTab === "saved" && !loading ? pagination.count : 0}
        />

        {isMine ? (
          <div className="mt-3.5">
            <MyAgentTenders embedded />
          </div>
        ) : (
          <>
            {/* Category pills + Cards/List toggle — ONE nowrap row */}
            <CategoryPills
              activeCategory={activeCategory}
              onChange={changeCategory}
              counts={tabCounts}
              className="mt-3.5"
            >
              <FeedViewToggle value={viewMode} onChange={changeViewMode} />
            </CategoryPills>

            {/* Filters — one row ≥1024 (artboard), two compact rows below */}
            <div className="hidden lg:flex items-center gap-2.5 mt-3">
              {searchInput}
              <FeedSegmented
                value={filters.type_of_tender || "all"}
                onChange={(v) =>
                  applyFilters({ ...filters, type_of_tender: v === "all" ? "" : v })
                }
              />
              <CustomDropdown
                options={FEED_LEVEL_CHOICES}
                value={filters.recruiting_for || ""}
                onChange={(v) => applyFilters({ ...filters, recruiting_for: v })}
                placeholder="All levels"
                displayProperty="label"
                valueProperty="value"
                {...v2DropdownProps(false)}
                chevronIcon={<ChevronGlyph size={11} />}
              />
              <CustomDropdown
                options={FEED_SORT_CHOICES}
                value={filters.sort_by || "newest"}
                onChange={(v) => applyFilters({ ...filters, sort_by: v })}
                displayProperty="label"
                valueProperty="value"
                {...v2DropdownProps(false)}
                chevronIcon={<ChevronGlyph size={11} />}
              />
              <label className="inline-flex items-center gap-[7px] text-[12.5px] text-[#334155] cursor-pointer select-none whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={!!filters.verified}
                  onChange={toggleVerifiedOnly}
                  className="w-[15px] h-[15px] rounded border-[1.5px] border-[#CBD5E1] accent-[#2283FF]"
                />
                Verified only
              </label>
              <button
                onClick={() => setShowFilterModal(true)}
                className="inline-flex items-center gap-[7px] h-9 border border-[#E2E8F0] rounded-[9px] bg-white px-3 text-[12.5px] font-semibold text-[#334155] hover:bg-gray-50"
              >
                <FunnelGlyph size={13} />
                Filters
              </button>
              {clearAllButton}
            </div>
            <div className="flex lg:hidden flex-col gap-[9px] mt-3">
              <div className="flex gap-[9px]">
                {searchInput}
                <button
                  onClick={() => setShowFilterModal(true)}
                  aria-label="Filters"
                  title="Filters"
                  className="flex-none w-9 h-9 border border-[#E2E8F0] rounded-[9px] bg-white inline-flex items-center justify-center text-[#334155] hover:bg-gray-50"
                >
                  <FunnelGlyph size={14} />
                </button>
              </div>
              <div className="flex gap-[9px] items-center">
                <FeedSegmented
                  compact
                  value={filters.type_of_tender || "all"}
                  onChange={(v) =>
                    applyFilters({ ...filters, type_of_tender: v === "all" ? "" : v })
                  }
                />
                <CustomDropdown
                  options={FEED_LEVEL_CHOICES}
                  value={filters.recruiting_for || ""}
                  onChange={(v) => applyFilters({ ...filters, recruiting_for: v })}
                  placeholder="All levels"
                  displayProperty="label"
                  valueProperty="value"
                  {...v2DropdownProps(true)}
                  chevronIcon={<ChevronGlyph size={10} />}
                />
                <CustomDropdown
                  options={FEED_SORT_CHOICES_COMPACT}
                  value={filters.sort_by || "newest"}
                  onChange={(v) => applyFilters({ ...filters, sort_by: v })}
                  displayProperty="label"
                  valueProperty="value"
                  {...v2DropdownProps(true)}
                  chevronIcon={<ChevronGlyph size={10} />}
                />
                {clearAllButton}
              </div>
            </div>

            {/* Hint line — verbatim green shield + fixed copy */}
            <div className="flex items-center gap-1.5 mt-[11px] text-[12.5px] text-[#64748B]">
              <HintShieldGlyph />
              Club-verified requests are shown first.
            </div>

            {/* Content */}
            {loading ? (
              <div className="flex justify-center py-20 text-gray-500">Loading…</div>
            ) : error ? (
              <div className="flex flex-col items-center py-20 text-center">
                <p className="text-red-500 mb-3">{error}</p>
                <button
                  onClick={() => fetchTenders(1)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Try again
                </button>
              </div>
            ) : tenders.length === 0 ? (
              topTab === "saved" && !hasActiveFilters ? (
                <SavedEmptyState onBrowse={() => selectTab("all_listed")} />
              ) : (
                <EmptyState
                  verifiedOnly={filters.verified}
                  hasFilters={hasActiveFilters}
                />
              )
            ) : (
              <>
                {/* List view is a desktop surface — at ≤1024 the feed keeps
                    cards (design doc 01), so the list only renders from xl up
                    and the card grid fills in below that width. */}
                {viewMode === "list" && (
                  <div className="hidden xl:block">
                    <AgentRequestListView
                      tenders={tenders}
                      onSendPlayer={goPitch}
                      onToggleSave={toggleSave}
                      savingIds={savingIds}
                    />
                  </div>
                )}
                {/* Card grid at the 7 design widths: 1 col mobile · 2 cols
                    768/1024 · 3 cols from 1280 (halfscreen — the historic
                    overflow bug) · 4 cols ≥1920 capped 1800px · 5 cols ≥2560
                    capped 2160px (no dead whitespace, no stretched cards).
                    Gaps per artboard: 16 / 18 / 20. */}
                <div
                  className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 min-[1920px]:grid-cols-4 min-[2560px]:grid-cols-5 gap-4 md:gap-[18px] xl:gap-5 w-full min-[1920px]:max-w-[1800px] min-[2560px]:max-w-[2160px] mx-auto mt-3.5 ${
                    viewMode === "list" ? "xl:hidden" : ""
                  }`}
                >
                  {tenders.map((tender) => (
                    <AgentTenderCard
                      key={tender.id}
                      tender={tender}
                      onSendPlayer={goPitch}
                      onToggleSave={toggleSave}
                      saving={savingIds.has(tender.id)}
                    />
                  ))}
                </div>
                <Pagination pagination={pagination} onPage={(p) => fetchTenders(p)} />
              </>
            )}
          </>
        )}
      </div>

      <AgentTenderFilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onApply={applyFilters}
        initialFilters={filters}
      />
    </div>
  );
};

export default AgentTenderFeed;
